import React, { useState, useEffect } from "react";
import { useRoute } from "@react-navigation/native";
import { RootRouteProps } from "routers/types/stack";
import { _pop, _goToBack, _goToAddNewFactorItems } from "routers/helper";

import { TouchableOpacity, View } from "react-native";

import {
  Icon,
  Text,
  Toast,
  Error,
  Header,
  Button,
  Loading,
  ExportPDF,
  LoadingBox,
  AddItemButton,
} from "components";

import Items from "./items";
import FactorSteps from "./FactorSteps";
import ItemsSectionTitle from "./ItemsSectionTitle";
import ChangeFactorMainStatus from "./ChangeFactorMainStatus";
import DetailsTemplate from "screens/components/DetailsTemplate";

import { useForm } from "react-hook-form";

import {
  useFactor,
  useAddFactor,
  useEditFactor,
  useDeleteFactor,
  useChangeFactorStep,
  useChangeFactorMainStatus,
} from "clients/apis";

import { useChangeStepFlags } from "./hooks/useChangeStepFlags";

import {
  changeArrayAt,
  isEqualArrays,
  alertPermission,
  removeArrayItemAt,
  makeFactorPdfTemplate,
  priceFormatter,
} from "utils";
import { authAtom, factorItemsAtom, factorStepsAtom } from "stores";
import { useFlag, useChmod, useInitialLoading } from "hooks";
import { sizes, colors, strings } from "assets/constants";
import { useRecoilState, useRecoilValue } from "recoil";
import css from "styles";
import FactorInfo from "./FactorInfo";

type ChangeStepDataListT = Array<{ id: number; status: string; count: number }>;

interface FormDataT {
  items: Array<FactorItemT>;
  status: string;
}

export default function Factor() {
  const { company } = useRecoilValue(authAtom);
  const steps = useRecoilValue(factorStepsAtom);
  const { chmod, factorStepsAllowed } = useChmod();

  const [factorItems, setFactorItems] = useRecoilState(factorItemsAtom);

  const {
    customer,
    data: routeData,
    activeStep,
  } = useRoute<RootRouteProps<"factor">>().params;

  const initial = routeData as FactorT;
  const headerTitle = initial
    ? strings.edit_factor + " " + initial?.customer?.name
    : strings.add_factor_for + " " + customer?.name;

  const allowedSteps = steps?.filter((e) =>
    factorStepsAllowed().some((k) => k === e.id)
  );

  const [nonEmptySteps, setNonEmptySteps] = useState<{ [key: string]: number }>(
    {}
  );

  // const [activeStepPriority, setActiveStepPriority] = useState(
  //   allowedSteps?.find((e) => e.id === activeStep?.id).priority
  // );
  const [activeStepPriority, setActiveStepPriority] = useState(
    allowedSteps?.find((e) => e.id === activeStep?.id)?.priority || 1
  );

  const [targetMultiChangeStep, setTargetMultiChangeStep] = useState(
    steps[steps.findIndex((e) => e.priority === activeStepPriority) + 1]
  );

  const [changeStepInfoList, setChangeStepInfoList] =
    useState<ChangeStepDataListT>([]);

  const {
    flg: changeMainStatus,
    on: showChangeMainStatus,
    off: hideChangeMainStatus,
  } = useFlag();

  const {
    flg: mainStatusModal,
    on: showMainStatusModal,
    off: hideMainStatusModal,
  } = useFlag();

  const {
    flg: changeStepLoading,
    on: startChangeStepLoading,
    off: stopChangeStepLoading,
  } = useFlag();

  const {
    closeItemChangeStepMode,
    multiItemsChangeStepMode,
    singleItemChangeStepModeForAll,
    countEditingMode,
  } = useChangeStepFlags();

  const { initialLoading } = useInitialLoading();
  const { isLoading, data, error, isError, refetch, isRefetching } = useFactor(
    initial?.id
  );

  const { setValue, getValues, handleSubmit } = useForm<FormDataT>({
    defaultValues: { items: [] },
  });

  useEffect(() => {
    if (data) {
      setFactorItems(data?.data?.items);
      setValue("items", data.data?.items);
      setValue("status", data?.data?.status);
      setNonEmptySteps(
        data?.data?.items?.reduce(
          (e: any, r: any) =>
            Object.assign(e, {
              [r.status?.id]: e[r.status?.id] > 0 ? e[r.status?.id] + 1 : 1,
            }),
          {}
        )
      );
    }
  }, [data, setValue]);

  useEffect(() => {
    !initialLoading &&
    !isLoading &&
    data?.data?.factor_status === strings.draft_awaiting_review
      ? showChangeMainStatus()
      : hideChangeMainStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, initialLoading]);

  useEffect(() => {
    return () => {
      setFactorItems([]);
      closeItemChangeStepMode();
    };
  }, []);

  const addFactorMut = useAddFactor();
  const editFactorMut = useEditFactor();
  const deleteFactorMut = useDeleteFactor();
  const changeFactorStepMut = useChangeFactorStep();
  const changeFactorMainStatusMut = useChangeFactorMainStatus();

  const _onChangeStep = (value: number) => {
    if (!countEditingMode) {
      closeItemChangeStepMode();
    }
    setActiveStepPriority(value);
    setTargetMultiChangeStep(
      steps[steps.findIndex((e) => e.priority === value) + 1]
    );
  };

  const _makePostData = () => {
    const items = factorItems;
    const postData: FactorReqT = {
      customer_id: customer?.id || data?.data.customer.id,
      items: [],
    };

    if (items?.length > 0) {
      const tmpItems = items?.map((item: FactorItemT) => ({
        id: item.id >= 0 ? item.id : null,
        category_id: item.category?.id,
        product_id: item.product?.id,
        count: item.count,
        attributes: item.attributes?.map((attr) => attr.id),
        description: item?.item_description || "",
      }));
      Object.assign(postData, { items: tmpItems });
    }

    return postData;
  };

  const _submitFactor = () => {
    // if (!getValues()?.items || getValues()?.items?.length === 0) {
    //   Toast.warning({ message: "ثبت فاکتور خالی، امکان پذیر نیست" });
    //   return;
    // }

    if (!factorItems || factorItems?.length === 0) {
      Toast.warning({ message: "ثبت فاکتور خالی، امکان پذیر نیست" });
      return;
    }
    addFactorMut.mutate(_makePostData(), {
      onSuccess: () => {
        closeItemChangeStepMode();
      },
    });
  };

  const _editFactor = () => {
    if (factorItems?.length > 0) {
      editFactorMut.mutate(
        { id: data?.data?.id, data: _makePostData() },
        {
          onSuccess: () => {
            refetch();
            closeItemChangeStepMode();
          },
        }
      );
    } else {
      Toast.warning({ message: "تغییر وضعیت فاکتور خالی، امکان پذیر نیست" });
    }
  };

  const _onDeleteConfirm = (close: () => void) =>
    deleteFactorMut.mutate(data?.data.id, {
      onSuccess: () => {
        closeItemChangeStepMode();
        close();
        _goToBack();
      },
      onError: () => {
        close();
      },
    });

  const _onAcceptFactor = (desc: string) => {
    hideMainStatusModal();
    const postData: ChangeFactorMainStatusReqT = {
      admin_description: desc,
      id: data?.data?.id,
      status: "ACCEPTED",
    };
    changeFactorMainStatusMut.mutate(postData, {
      onSuccess: () => {
        refetch();
        closeItemChangeStepMode();
      },
    });
  };
  const _onRejectFactor = (desc: string) => {
    hideMainStatusModal();

    const postData: ChangeFactorMainStatusReqT = {
      admin_description: desc,
      id: data?.data?.id,
      status: "REJECTED",
    };

    changeFactorMainStatusMut.mutate(postData, {
      onSuccess: () => {
        refetch();
      },
    });
  };

  const _getChangeStepData = (
    itemId: number,
    stepId: string,
    count: number
  ) => {
    const newData = { id: itemId, status: stepId, count };
    let tmp: ChangeStepDataListT;
    const index = changeStepInfoList?.findIndex((e) => e.id === itemId);

    if (index > -1) {
      tmp = changeArrayAt(changeStepInfoList, index, newData);
    } else {
      tmp = [...changeStepInfoList, newData];
    }
    setChangeStepInfoList(tmp);
  };

  const _removeItemFromChangeStepInfoList = (itemId: number) => {
    const index = changeStepInfoList?.findIndex((e) => e.id === itemId);
    if (index > -1) {
      const tmp = removeArrayItemAt(changeStepInfoList, index);
      setChangeStepInfoList(tmp);
    }
  };

  const _closeChangeStepMode = () => {
    closeItemChangeStepMode();
    setChangeStepInfoList([]);
  };

  const _onSubmitMultiChangeStep = () => {
    startChangeStepLoading();
    const postData = changeStepInfoList?.map((item) => ({
      ...item,
      status: targetMultiChangeStep?.id,
    }));

    changeFactorStepMut.mutate(
      { ids: postData },
      {
        onSettled(data, error, variables, context) {
          if (!error) {
            closeItemChangeStepMode();
            setChangeStepInfoList([]);
            refetch().then(stopChangeStepLoading);
          }
        },
      }
    );
  };

  const _onSubmitSingleChangeStep = () => {
    startChangeStepLoading();

    changeFactorStepMut.mutate(
      { ids: changeStepInfoList },
      {
        onSettled(data, error, variables, context) {
          if (!error) {
            closeItemChangeStepMode();
            setChangeStepInfoList([]);
            refetch().then(stopChangeStepLoading);
          }
        },
      }
    );
  };

  const _onSubmit = () => {
    if (multiItemsChangeStepMode) {
      _onSubmitMultiChangeStep();
      return;
    }

    if (singleItemChangeStepModeForAll) {
      _onSubmitSingleChangeStep();
      return;
    }

    if (data) {
      chmod("factor", "PUT") ? handleSubmit(_editFactor)() : alertPermission();
      return;
    }

    chmod("factor", "POST") ? handleSubmit(_submitFactor)() : alertPermission();
  };

  const _renderContent = () => {
    if (initial && (isLoading || initialLoading)) {
      return <Loading style={{ marginTop: 20 }} />;
    }

    if (isError) {
      return <Error error={error} reset={refetch} loading={isRefetching} />;
    }

    return (
      <>
        {changeMainStatus && (
          <TouchableOpacity
            onPress={showMainStatusModal}
            activeOpacity={sizes.tapOpacity}
            style={{
              top: -20,
              paddingTop: 30,
              paddingBottom: 15,
              backgroundColor: colors.darkWarning,
              zIndex: -1,
              ...css.rRowCenterAlign,
              paddingHorizontal: 10,
            }}
          >
            <Icon size={18} color={colors.white} name="alert-circle" />

            <Text color={colors.white} weight="bold" size={"sm"}>
              {" "}
              ابتدا وضعیت فاکتور را مشخص کنید.{" "}
            </Text>
            <Text
              size={"sm"}
              weight="extraBold"
              style={{ ...css.textUnderline }}
              color={colors.white}
            >
              تعیین وضعیت فاکتور
            </Text>
          </TouchableOpacity>
        )}

        {data && (
          <FactorInfo
            lastUpdate={data?.data?.updated_at}
            factorDescription={data?.data?.factor_description}
          />
        )}

        {data && steps && (
          <FactorSteps
            activeStepPriority={activeStepPriority}
            onChangeStep={_onChangeStep}
            nonEmptySteps={nonEmptySteps}
          />
        )}

        <DetailsTemplate
          onDelete={_onDeleteConfirm}
          deletable={
            !(multiItemsChangeStepMode || singleItemChangeStepModeForAll) &&
            data?.data?.factor_status === strings.accepted &&
            data &&
            chmod("factor", "DELETE")
              ? true
              : false
          }
          deleteLoading={deleteFactorMut?.isLoading}
          hideSubmit={
            !data ? false : data?.data?.factor_status !== strings.accepted
          }
          submitLabel={!data ? strings.submit_info : strings.edit_info}
          submitLoading={addFactorMut?.isLoading || editFactorMut?.isLoading}
          onSubmit={_onSubmit}
          preventChange={
            data?.data?.status === "DONE" || data?.data?.status === "CANCELED"
          }
        >
          <>
            <ItemsSectionTitle
              onClose={_closeChangeStepMode}
              defaultStepPriority={activeStepPriority}
              onChangeTargetStep={setTargetMultiChangeStep}
              hideStepChanger={
                !chmod("factor", "PUT") ||
                data?.data?.factor_status !== strings.accepted ||
                factorItems?.filter(
                  (e) => e.status.priority === activeStepPriority
                ).length === 0 ||
                countEditingMode
              }
            />

            <Items
              items={factorItems}
              onChange={setFactorItems}
              activeStepPriority={activeStepPriority}
              getChangeStepData={_getChangeStepData}
              onCloseChangeStep={_removeItemFromChangeStepInfoList}
              disableAction={
                data ? data?.data?.factor_status !== strings.accepted : false
              }
            />

            {chmod("factor", "POST") &&
              activeStepPriority === 1 &&
              !(multiItemsChangeStepMode || singleItemChangeStepModeForAll) &&
              (data
                ? data?.data?.factor_status === strings.accepted
                : true) && (
                <AddItemButton
                  onPress={() => {
                    _goToAddNewFactorItems({
                      items: getValues().items?.filter(
                        (e) => e?.status?.priority === 1
                      ),
                    });
                  }}
                />
              )}
          </>
        </DetailsTemplate>

        {data?.data?.items.length > 0 &&
          !(multiItemsChangeStepMode || singleItemChangeStepModeForAll) && (
            <ExportPDF
              button={{
                title: { value: "PDF" },
                circle: 50,
                bottom: 60,
                left: 20,
              }}
              template={makeFactorPdfTemplate(
                data?.data,
                company?.name,
                company?.avatar
              )}
              extraData={data?.data}
            />
          )}

        {data?.data?.factor_status === strings.rejected && (
          <Button
            style={{ borderRadius: 0, height: 60 }}
            disabled
            title={{ value: "سفارش رد شده", size: "xxl" }}
            bgColor={colors.danger}
          />
        )}
      </>
    );
  };

  return (
    <>
      <Header title={headerTitle} />
      {_renderContent()}

      <ChangeFactorMainStatus
        visible={mainStatusModal}
        hide={hideMainStatusModal}
        onAccept={_onAcceptFactor}
        onReject={_onRejectFactor}
      />

      {(changeStepLoading || changeFactorMainStatusMut.isLoading) && (
        <LoadingBox />
      )}
    </>
  );
}
